import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOnboardingStore } from '../store/onboardingStore';
import { toBase64 } from '../utils/utils';
import { uploadImagesToS3 } from '../services/s3/s3Service';
import { dictionary } from '../utils/dictionary';
import toast from 'react-hot-toast';

interface UseImageUploadProps {
  maxImages: number;
  maxFileSize: number;
  folder: string;
}

interface UseImageUploadReturn {
  isLoading: boolean;
  error: string;
  handleImageUpload: () => void;
  removeImage: (index: number) => void;
  selectMainImage: (index: number) => void;
  selectedImageIndex: number;
  setError: (error: string) => void;
  uploadProgress: {
    current: number;
    total: number;
    isUploading: boolean;
  };
}

export const useImageUpload = ({
  maxImages,
  maxFileSize,
  folder
}: UseImageUploadProps): UseImageUploadReturn => {
  const { language } = useAuth();
  const { data, updateVisualContent } = useOnboardingStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    isUploading: false
  });

  const handleImageUpload = () => {
    const currentImageCount = data.visualContent.images.length;
    const remainingSlots = maxImages - currentImageCount;
    
    if (remainingSlots <= 0) {
      setError(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }
  
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png, image/jpg, image/jpeg, image/webp';
    fileInput.multiple = true;
  
    fileInput.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;
  
      const remainingSlots = maxImages - currentImageCount;
      if (remainingSlots <= 0) {
        setError(`Ya alcanzaste el límite de ${maxImages} imágenes`);
        return;
      }
  
      const filesToUpload = Array.from(files).slice(0, remainingSlots);
      const validFiles = filesToUpload.filter(file => file.size <= maxFileSize);
  
      if (validFiles.length < filesToUpload.length) {
        toast.error(dictionary["max-file-size-per-image"][language] || "El límite de tamaño por imagen es de 1MB");
      }
  
      setIsLoading(true);
      setError('');
      setUploadProgress({
        current: 0,
        total: validFiles.length,
        isUploading: true
      });
      
      try {
        const uploadedUrls: string[] = [];

        for (let i = 0; i < validFiles.length; i++) {
          const file = validFiles[i];
          
          setUploadProgress(prev => ({
            ...prev,
            current: i + 1
          }));

          const base64File = await toBase64(file);

          const s3Response = await uploadImagesToS3({
            file: base64File as string,
            folder: folder,
            filename: `${Date.now()}-${file.name}`
          });

          uploadedUrls.push(s3Response.s3_url);
        }

        updateVisualContent({
          images: [...data.visualContent.images, ...uploadedUrls]
        });

        toast.success(`${uploadedUrls.length} imagen(es) subida(s) exitosamente`);

      } catch (error) {
        console.error("Error al subir las imágenes:", error);
        setError("Error al subir las imágenes a S3");
        toast.error("Error al subir las imágenes");
      } finally {
        setIsLoading(false);
        setUploadProgress({
          current: 0,
          total: 0,
          isUploading: false
        });
      }
    };
  
    fileInput.click();
  };

  const removeImage = (index: number) => {
    const newImages = [...data.visualContent.images];
    newImages.splice(index, 1);
    
    updateVisualContent({ images: newImages });
    
    if (selectedImageIndex === index) {
      setSelectedImageIndex(Math.max(0, selectedImageIndex - 1));
    } else if (selectedImageIndex > index) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const selectMainImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  return {
    isLoading,
    error,
    handleImageUpload,
    removeImage,
    selectMainImage,
    selectedImageIndex,
    setError,
    uploadProgress
  };
};
